/**
 * All Evidence Tab Component
 * Complete evidence list with approval status
 */
import React, { useState } from 'react';
import { 
  FileText, 
  User, 
  Clock,
  CheckCircle2,
  XCircle,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ApprovalChainVisualizer from './ApprovalChainVisualizer';

const STATUS_CONFIG = {
  pending: { label: 'Pending', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  pending_l1: { label: 'L1 Pending', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  pending_l2: { label: 'L2 Pending', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  pending_l3: { label: 'L3 Pending', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  approved: { label: 'Approved', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  rejected: { label: 'Rejected', bg: 'bg-red-500/20', text: 'text-red-400' },
};

export default function AllEvidenceTab({ 
  evidence, 
  isLoading, 
  onPageChange,
  currentPage = 1,
  totalPages = 1,
  statusFilter,
  onStatusFilterChange 
}) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  
  const items = evidence?.items || [];
  const filteredItems = items.filter(item => 
    item.fileName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.owner?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  if (isLoading) {
    return (
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
        <div className="p-4 space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex items-center gap-4 p-3 animate-pulse">
              <div className="w-8 h-8 bg-[#2F2F2F] rounded" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-[#2F2F2F] rounded w-1/3" />
                <div className="h-3 bg-[#2F2F2F] rounded w-1/4" />
              </div>
              <div className="w-20 h-6 bg-[#2F2F2F] rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search by filename or owner..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none"
            data-testid="evidence-search-input"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#6B6B6B]" />
          <select
            value={statusFilter || 'all'}
            onChange={(e) => onStatusFilterChange && onStatusFilterChange(e.target.value)}
            className="px-3 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
            data-testid="status-filter-select"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
      
      {/* Table */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#1F1F1F]">
              <th className="px-4 py-3 text-left text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                Evidence
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                Owner
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                Last Approver
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                Uploaded
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1F1F1F]">
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center">
                  <FileText size={32} className="mx-auto text-[#2F2F2F] mb-3" />
                  <p className="text-sm text-[#6B6B6B]">No evidence found</p>
                </td>
              </tr>
            ) : (
              filteredItems.map(item => (
                <tr 
                  key={item.id} 
                  className="hover:bg-[#1A1A1A] transition-colors"
                  data-testid={`evidence-row-${item.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-[#1F1F1F]">
                        <FileText size={14} className="text-[#6B6B6B]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate max-w-[200px]" title={item.fileName}>
                          {item.fileName}
                        </p>
                        {item.category && (
                          <span className="text-xs text-[#6B6B6B]">{item.category}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <User size={12} className="text-[#6B6B6B]" />
                      <span className="text-sm text-white">{item.owner?.name || 'Unknown'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.approvalStatus} />
                  </td>
                  <td className="px-4 py-3">
                    {item.lastApprover ? (
                      <div className="text-xs">
                        <p className="text-white">{item.lastApprover.name}</p>
                        <p className="text-[#6B6B6B]">L{item.lastApprover.level}</p>
                      </div>
                    ) : (
                      <span className="text-xs text-[#4F4F4F]">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-[#6B6B6B]">
                      {item.uploadedAt ? new Date(item.uploadedAt).toLocaleDateString() : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => navigate(`/evidence/${item.id}`)}
                      className="p-2 hover:bg-[#2F2F2F] rounded-lg transition-colors text-[#6B6B6B] hover:text-white"
                      title="View Evidence"
                    >
                      <ExternalLink size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-[#1F1F1F] flex items-center justify-between">
            <p className="text-xs text-[#6B6B6B]">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onPageChange && onPageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="p-1.5 rounded-lg hover:bg-[#1F1F1F] disabled:opacity-50 disabled:cursor-not-allowed text-[#6B6B6B]"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => onPageChange && onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="p-1.5 rounded-lg hover:bg-[#1F1F1F] disabled:opacity-50 disabled:cursor-not-allowed text-[#6B6B6B]"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}
